import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FileText, ArrowRight } from 'lucide-react';
import { Container } from '../components/ui';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import CTASection from '../components/CTASection';
import { LEGAL_DOCS, LEGAL_INDEX, LEGAL_ENTITY } from '../data/legal';

function slugify(h) {
  return h.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function LegalIndex() {
  return (
    <div data-testid="legal-index">
      <PageHero eyebrow="Legal" title="Legal & compliance" subtitle={`Policies and agreements governing your use of BillPortal, provided by ${LEGAL_ENTITY}`} />
      <section className="px-5 py-16 sm:px-8 sm:py-20">
        <Container>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {LEGAL_INDEX.map((d, i) => (
              <Reveal key={d.slug} delay={i * 50}>
                <Link
                  to={`/legal/${d.slug}`}
                  data-testid={`legal-card-${d.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-lg dark:border-white/10 dark:bg-white/[0.03]"
                >
                  <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                    <FileText className="h-5 w-5" />
                  </span>
                  <h3 className="font-semibold text-slate-900 dark:text-white">{d.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{d.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
                    Read <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>
      <CTASection />
    </div>
  );
}

export default function Legal() {
  const { slug } = useParams();
  const doc = slug ? LEGAL_DOCS[slug] : null;

  const toc = useMemo(() => (doc ? doc.sections.map((s) => ({ id: slugify(s.h), label: s.h })) : []), [doc]);

  if (!slug || !doc) return <LegalIndex />;

  return (
    <div data-testid={`legal-doc-${doc.slug}`}>
      <PageHero eyebrow={doc.eyebrow} title={doc.title} subtitle={doc.summary} />

      <section className="px-5 py-14 sm:px-8 sm:py-16">
        <Container>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            {/* TOC */}
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-24">
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">On this page</div>
                <nav className="mt-4 space-y-2" data-testid="legal-toc">
                  {toc.map((it) => (
                    <a
                      key={it.id}
                      href={`#${it.id}`}
                      className="block text-sm leading-snug text-slate-500 transition-colors hover:text-brand dark:text-slate-400"
                    >
                      {it.label}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Content */}
            <div className="lg:col-span-9">
              <div className="mb-8 text-sm text-slate-500 dark:text-slate-400">Last updated: {doc.updated}</div>

              <div className="mb-8 flex flex-col items-start justify-between gap-3 rounded-xl border border-brand/20 bg-brand/[0.04] p-4 sm:flex-row sm:items-center" data-testid="legal-support-banner">
                <p className="text-sm text-slate-600 dark:text-slate-300">Questions about this policy, a refund, or want to raise a dispute? Our team is happy to help.</p>
                <Link to="/support" data-testid="legal-support-link" className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-600">
                  Contact support <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              <div className="space-y-10">
                {doc.sections.map((s) => (
                  <div key={s.h} id={slugify(s.h)} className="scroll-mt-24">
                    <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white sm:text-2xl">{s.h}</h2>
                    {s.body && s.body.map((p, i) => (
                      <p key={i} className="mt-3 text-base leading-relaxed text-slate-600 dark:text-slate-300">{p}</p>
                    ))}
                    {s.bullets && (
                      <ul className="mt-4 space-y-2.5">
                        {s.bullets.map((b, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-base leading-relaxed text-slate-600 dark:text-slate-300">
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-12 border-t border-slate-200 pt-6 dark:border-white/10">
                <Link to="/legal" data-testid="legal-back-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline">
                  <ArrowRight className="h-4 w-4 rotate-180" /> All legal documents
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
